import React, { useState, useEffect } from 'react';
import { ArrowDownIcon } from 'cokitchen-icons';
import { ReactComponent as EditMealIcon } from 'assets/icons/edit-meal-icon.svg';
import { ReactComponent as DuplicateIcon } from 'assets/icons/duplicate-meal-icon.svg';
import { ReactComponent as ArchiveIcon } from 'assets/icons/archive-icon.svg';
import './style.scss';
import { AddonsType, MealAddonsType } from 'utils/types';
import { Link, useLocation } from 'react-router-dom';
import { ROUTE_KEYS } from 'utils/constants';
import { Button, Input, ToastMessage } from '@cokitchen/cokitchen-components';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import { SortableContext, arrayMove } from '@dnd-kit/sortable';
import { DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import OrganizeItem from '../organize-item';
import Addon from '../addon';
import DeleteModal from '../delete-modal';
import useDashboardContext from 'contexts/dashboard.context';

type Props = {
  title: string;
  addons?: MealAddonsType[];
  id: string;
  mealId: string;
  className?: string;
  checked?: boolean;
  onChange?: () => void;
  addonCategoryList?: AddonsType[];
  onRefresh?: () => Promise<void>;
  onRestoreComplete?: (arg0: string) => void;
};

const AddonAccordion = ({
  title,
  addons,
  id,
  className = '',
  checked,
  onChange,
  mealId,
  onRestoreComplete,
  addonCategoryList,
  onRefresh
}: Props) => {
  const { routePrefix } = useDashboardContext();
  const [show, setShow] = useState<string | null>(null);
  const [cardHovered, setCardHovered] = useState<string | null>(null);
  const baseUrl = `${routePrefix}/${ROUTE_KEYS.MEALS}/${mealId}/addon-categories/${id}`;
  const duplicateUrl = `${baseUrl}/duplicate-addon-category/${title?.replace('/', '%2F')}`;
  const updateUrl = `${baseUrl}/update-addon-category/${title?.replace('/', '%2F')}`;

  const selectedAddon = addons?.find((addon) => addon.id === cardHovered);
  const baseDuplicateAddonUrl = `${routePrefix}/${ROUTE_KEYS.MEALS}/${mealId}/addon-categories/${selectedAddon?.id}/duplicate-addon`;

  const { pathname, state: addonId } = useLocation();
  const [showDeleteAddonModal, setShowDeleteAddonModal] = useState(false);
  const [selectAddonId, setSelectAddonId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [showDeleteAddonCategoryModal, setShowDeleteAddonCategoryModal] = useState(false);
  const [selectAddonCategoryId, setSelectAddonCategoryId] = useState<string>('');
  const [showRestoreCategoryModal, setShowRestoreCategoryModal] = useState(false);
  const [activeAddonId, setActiveAddonId] = useState<string>('');
  const [dndActive, setDndActive] = useState(false);
  const [swaps, setSwaps] = useState(0);
  const [addonsList, setAddonsList] = useState<MealAddonsType[]>(addons ?? []);
  const [updatingPositions, setUpdatingPositions] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8
      }
    })
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over, collisions } = event;

    if (!collisions?.length) return;
    if (active.id === over?.id) return;
    setSwaps((s) => s + 1);
    setAddonsList((a) => {
      const activeIndex = a?.findIndex((a) => a.id === active.id);
      const overIndex = a?.findIndex((a) => a.id === over?.id);
      return arrayMove(a, activeIndex, overIndex);
    });
  }

  async function updateAddonPositions() {
    const payload = addonsList.reduce<{
      meal_addon_category_id: string;
      addons: string[];
    }>(
      (payload, addon) => {
        payload.addons.push(addon.meal_data.id);
        return payload;
      },
      {
        meal_addon_category_id: id,
        addons: []
      }
    );

    setUpdatingPositions(true);
    try {
      await apiService.put('marketing/addon/position', { data: payload });
      setSwaps(0);
      setDndActive(false);
      setUpdatingPositions(false);
    } catch {
      setUpdatingPositions(false);
    }
  }

  const handleRestoreClick = async () => {
    const payload = { type: 'addon_category', item_ids: [id] };
    try {
      const res: any = await apiService.post('marketing/restore-bulk-items', { data: payload });
      if (res.data) {
        onRestoreComplete?.(id);
        restoreCategory();
        toast(<ToastMessage type='success' message={`Addon Category restored successfully `} />);
      }
    } catch (err: any) {
      toast(<ToastMessage type='error' message={err.response.data.message} />);
    }
  };

  const handleDeleteAddon = async () => {
    setLoading(true);
    try {
      const payload = {
        addon_ids: [selectAddonId]
      };
      await apiService.post(`marketing/delete-addon/bulk`, { data: payload });
      toast(<ToastMessage type='success' message='Addon deleted successfully' />);
      setAddonsList(addonsList.filter((addon) => addon.id !== selectAddonId));
    } catch (err: any) {
      toast(<ToastMessage type='error' message={err.response.data.message} />);
    } finally {
      setLoading(false);
      setShowDeleteAddonModal(false);
    }
  };

  const handleDeleteAddonCategory = async () => {
    try {
      setLoading(true);
      await apiService.delete('marketing/bulk-delete-meal-addon-category', {
        data: {
          meal_addon_category_ids: [id]
        }
      });
      toast(<ToastMessage type='success' message='Successfully deleted addon category' />);
      onRefresh?.();
    } catch {
      toast(<ToastMessage type='error' message='Error occur while deleting item' />);
    } finally {
      setLoading(false);
      setShowDeleteAddonCategoryModal(false);
    }
  };

  const restoreCategory = () => {
    setShowRestoreCategoryModal(!showRestoreCategoryModal);
  };

  const isSearch = () => {
    return pathname.includes('search');
  };

  const handleShow = (id: string) => {
    if (show) {
      setShow(null);
    } else {
      setShow(id);
    }
  };

  useEffect(() => {
    if (addonId) {
      setShow(addonId);
    }
  }, [addonId]);

  return (
    <div className={`accordion ${className}`}>
      <div
        className='flex justify-between items-center accordion_header'
        onClick={() => handleShow(id)}>
        <div className='flex gap-4 items-center'>
          <div className='flex gap-2 items-center'>
            {isSearch() && (
              <Input
                type='checkbox'
                name='new'
                checked={checked}
                checkColor='gray'
                onClick={(e) => {
                  e.stopPropagation();
                  onChange?.();
                }}
                className=' mr-3 z-40'
              />
            )}

            <div className='flex gap-2'>
              <Link
                to={updateUrl}
                className='bg-white border border-gray-13 w-8 h-8 rounded-full flex justify-center items-center cursor-pointer hover:bg-gray-1 bg-opacity-70'>
                <EditMealIcon />
              </Link>
              <Link
                to={duplicateUrl}
                className='bg-white border border-gray-13 w-8 h-8 rounded-full flex justify-center items-center cursor-pointer hover:bg-gray-1 bg-opacity-70'>
                <DuplicateIcon />
              </Link>
              <div
                className='bg-white border border-gray-13 text-[#858585] w-8 h-8 rounded-full flex justify-center items-center cursor-pointer hover:bg-gray-1 bg-opacity-70'
                onClick={() => {
                  setSelectAddonCategoryId(id);
                  setShowDeleteAddonCategoryModal(true);
                }}>
                <ArchiveIcon />
              </div>
            </div>
          </div>
          <p className='text-base font-medium'>{title}</p>
        </div>
        <ArrowDownIcon />
      </div>
      {show === id && (
        <div className='accordion_body mt-6'>
          {addons?.length === 0 ? (
            <p className='font-medium'>There are no addons for {title}</p>
          ) : (
            <div className='w-full flex flex-col gap-3'>
              <div className='h-10 self-end'>
                {dndActive ? (
                  <div className='flex items-center gap-2'>
                    <Button
                      small
                      className='flex justify-end h-10 w-[100px] ml-auto'
                      variant='outline'
                      color='primary'
                      disabled={updatingPositions}
                      onClick={() => {
                        setDndActive(false);
                        setAddonsList(addons ?? []);
                        setSwaps(0);
                      }}>
                      Cancel
                    </Button>

                    <Button
                      small
                      className='flex justify-end h-10 w-[100px] ml-auto'
                      variant='block'
                      color='primary'
                      disabled={!swaps}
                      loading={updatingPositions}
                      onClick={updateAddonPositions}>
                      Save
                    </Button>
                  </div>
                ) : (
                  <OrganizeItem text='addons' onClick={() => setDndActive(true)} />
                )}
              </div>
              <div className='grid gap-3 grid-cols-[repeat(auto-fill,_minmax(167px,_1fr))]'>
                {dndActive && !updatingPositions ? (
                  <DndContext onDragEnd={handleDragEnd} sensors={sensors}>
                    <SortableContext items={addons ?? []}>
                      {addonsList?.map((item: MealAddonsType) => (
                        <Addon
                          key={item.id}
                          item={item}
                          dndActive={dndActive}
                          cardHovered={cardHovered}
                          setCardHovered={setCardHovered}
                          duplicateUrl={baseDuplicateAddonUrl}
                          activeAddonId={activeAddonId}
                          setActiveAddonId={setActiveAddonId}
                          setSelectAddonId={setSelectAddonId}
                          setShowDeleteAddonModal={setShowDeleteAddonModal}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                ) : (
                  addonsList?.map((item: MealAddonsType) => (
                    <Addon
                      key={item.id}
                      item={item}
                      dndActive={dndActive}
                      cardHovered={cardHovered}
                      setCardHovered={setCardHovered}
                      duplicateUrl={baseDuplicateAddonUrl}
                      activeAddonId={activeAddonId}
                      setActiveAddonId={setActiveAddonId}
                      setSelectAddonId={setSelectAddonId}
                      setShowDeleteAddonModal={setShowDeleteAddonModal}
                    />
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      )}

      <DeleteModal
        show={showDeleteAddonModal}
        onClose={() => setShowDeleteAddonModal(false)}
        handleDelete={handleDeleteAddon}
        description='This action cannot be undone'
        title={`Are you sure you want to archive ${
          addons?.find((item) => item.id === selectAddonId)?.name ?? ''
        } ?`}
        loading={loading}
      />
      <DeleteModal
        show={showDeleteAddonCategoryModal}
        onClose={() => setShowDeleteAddonCategoryModal(false)}
        handleDelete={handleDeleteAddonCategory}
        description='This action cannot be undone'
        title={`Are you sure you want to archive ${
          addonCategoryList?.find((item) => item.id === selectAddonCategoryId)?.name ?? ''
        } ?`}
        loading={loading}
      />
      <DeleteModal
        show={showRestoreCategoryModal}
        onClose={restoreCategory}
        handleDelete={() => handleRestoreClick()}
        title={`Are you sure you want to restore ${title} ?`}
        description='This action will restore this category'
        loading={loading}
        type='restore'
      />
    </div>
  );
};

export default AddonAccordion;
